import { create } from 'zustand';
import { ref, query, orderByChild, limitToLast, onValue } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { isSlaBreached } from '../logic/priority';
import { recordRead, approxBytes, makeSyncRecorder } from '../../../integrations/rtdbMeter';

import { deltaCursor, mergeTicketRows, needsFullResync } from '../logic/ticketListSync';

const recordTicketSignal = makeSyncRecorder('/tickets live signal');

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
const SAFETY_MS = 120_000;      // backstop only (a DELTA — a few bytes when nothing moved)
const FULL_RESYNC_MS = 60 * 60_000;   // whole list again, for anything a delta cannot see
const DEBOUNCE_MS = 400;

/**
 * The ticket LIST.
 *
 * This used to open an onValue on the entire `tickets` tree. Measured on live
 * data that is 163 tickets / 0.89MB, and the Support page then fired one read
 * per distinct customer for their last telecaller remark — 121 more round
 * trips. That is what the spinner was waiting for.
 *
 * 85% of those bytes are transcripts, internal notes, audit logs, customer
 * messages and AI overviews, which the list never renders; they are read when
 * a ticket is OPENED. The list now comes from /api/complaints/list, which
 * strips them, merges the remark server-side, and answers in one request.
 *
 * It is still LIVE. Rather than streaming the whole tree, it watches the single
 * most recently updated ticket (orderByChild('updatedAt').limitToLast(1)) —
 * about 5KB — and refetches the compact list whenever that changes. Every
 * ticket mutation stamps updatedAt, so a new ticket or any edit arrives within
 * a moment, at a fraction of the cost of subscribing to all 163.
 *
 * 29 Oct 2026 — the sidebar meter showed "Support / Complaints 27 MB" for one
 * session. Each of those refetches was the WHOLE ~170 KB list: every write to any
 * ticket in the office (a note, a status change, the drawer's own edits) and the
 * 2-minute backstop each pulled all 210 rows again — ~160 full lists. Now the
 * list is fetched in full ONCE (and hourly as a re-sync); the live
 * signal and the backstop ask only for tickets touched since the newest one held
 * (`?since=`), which is usually zero or one row.
 */
export const useComplaintStore = create((set, get) => ({
    tickets: [],
    loading: true,
    error: '',
    _timer: null,
    _unsub: null,
    _subCount: 0,
    _inFlight: null,
    _again: false,
    _forceFull: false,
    _lastFullAt: 0,

    /**
     * Bring the list up to date. A delta when we already hold a list, the whole
     * list when we do not (or `{ full: true }`). Calls that arrive while one is in
     * flight collapse into a single follow-up instead of stacking requests.
     */
    refresh: async ({ full = false } = {}) => {
        if (get()._inFlight) { set({ _again: true, _forceFull: get()._forceFull || full }); return get()._inFlight; }
        const run = (async () => {
            try {
                const held = get().tickets;
                const since = full || !held.length ? 0 : deltaCursor(held);
                const { authHeaders } = await import('../../../core/api/authHeaders');
                const url = `${PROXY_BASE}/api/complaints/list${since ? `?since=${since}` : ''}`;
                const r = await fetch(url, { headers: await authHeaders() });
                const j = await r.json().catch(() => ({}));
                if (!r.ok || !j.ok) throw new Error(j.error || `Ticket list failed (${r.status})`);
                const rows = j.rows || [];
                try { recordRead(since ? '/api/complaints/list (changes only)' : '/api/complaints/list (full)', approxBytes(rows)); } catch { /* ignore */ }
                if (!since) {
                    set({ tickets: rows, loading: false, error: '', _lastFullAt: Date.now() });
                    return;
                }
                const merged = mergeTicketRows(held, rows);
                if (merged !== held) set({ tickets: merged });
                set({ loading: false, error: '' });
                // A deleted ticket (or a missed write) leaves our count off the
                // server's — only then is the whole list worth fetching again.
                if (needsFullResync(merged, j.count)) set({ _again: true, _forceFull: true });
            } catch (e) {
                set({ loading: false, error: e.message });
            }
        })();
        set({ _inFlight: run });
        try { await run; } finally {
            const again = get()._again;
            const forceFull = !!get()._forceFull;
            set({ _inFlight: null, _again: false, _forceFull: false });
            if (again) await get().refresh({ full: forceFull });
        }
    },

    /** The whole list again (pull-to-refresh, periodic re-sync). */
    resync: () => get().refresh({ full: true }),

    // Ref-counted so several panels can mount without each opening a watcher.
    subscribe: () => {
        const n = get()._subCount + 1;
        set({ _subCount: n });
        if (n > 1) return () => get().unsubscribe();

        // A list we already hold (the page was left and re-opened) is brought up
        // to date with a delta; only a cold store downloads the whole list.
        const stale = Date.now() - get()._lastFullAt > FULL_RESYNC_MS;
        get().refresh({ full: stale });

        // LIVE: watch only the most recently touched ticket. Any create or edit
        // stamps updatedAt, so this fires on every change while transferring
        // one ticket instead of all of them — and what it triggers is a delta.
        let debounce = null;
        let first = true;
        const q = query(ref(db, 'tickets'), orderByChild('updatedAt'), limitToLast(1));
        const unsub = onValue(q, (snap) => {
            recordTicketSignal(snap.val());
            if (first) { first = false; return; }      // the initial echo of what we just fetched
            clearTimeout(debounce);
            debounce = setTimeout(() => get().refresh(), DEBOUNCE_MS);
        }, () => { /* a failed watcher must not stop the safety refresh */ });

        // Backstop: a cheap delta every 2 minutes, and the whole list hourly for
        // anything that somehow writes without stamping updatedAt.
        const timer = setInterval(() => {
            get().refresh({ full: Date.now() - get()._lastFullAt > FULL_RESYNC_MS });
        }, SAFETY_MS);
        set({ _timer: timer, _unsub: () => { clearTimeout(debounce); unsub(); } });
        return () => get().unsubscribe();
    },

    unsubscribe: () => {
        const n = Math.max(0, get()._subCount - 1);
        set({ _subCount: n });
        if (n > 0) return;
        const { _timer, _unsub } = get();
        if (_timer) clearInterval(_timer);
        if (_unsub) _unsub();
        set({ _timer: null, _unsub: null });
    },

    // KPI counts (section 1)
    getKpis: () => {
        const t = get().tickets;
        const now = Date.now();
        const isOpenish = (x) => x.status !== 'closed' && x.status !== 'resolved';
        return {
            total: t.length,
            open: t.filter(x => x.status === 'open').length,
            pending: t.filter(x => x.status === 'pending').length,
            escalated: t.filter(x => x.escalationLevel > 0 || x.status === 'courier_escalation').length,
            doctorReview: t.filter(x => x.status === 'doctor_review').length,
            courierEscalation: t.filter(x => x.status === 'courier_escalation').length,
            refundPending: t.filter(x => x.status === 'refund_pending' || x.refundCase?.refundStatus === 'pending' || x.refundCase?.refundStatus === 'approved').length,
            refundIssued: t.filter(x => x.refundCase?.refundStatus === 'issued').length,
            replacementPending: t.filter(x => x.status === 'replacement_pending' || ['approved', 'packed', 'shipped'].includes(x.replacementCase?.replacementStatus)).length,
            reshipPending: t.filter(x => (x.replacementCase?.newCourier && x.replacementCase?.replacementStatus !== 'delivered') || (x.issueType === 'Reship Required' && isOpenish(x))).length,
            returnPickupPending: t.filter(x => ['pending', 'scheduled'].includes(x.returnCase?.pickupStatus)).length,
            overdue: t.filter(x => isOpenish(x) && isSlaBreached(x, now)).length,
            closed: t.filter(x => x.status === 'closed').length,
        };
    },
}));

export default useComplaintStore;
